import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthForm from '@shared/components/AuthForm';
import Button from '@shared/components/Button';
import { InputText } from '@shared/components/InputText';
import Layout from '@shared/components/Layout';
import { usePublishScreenViewedEvent } from '@shared/utils/analytics/hooks';
import { validateEmailOrUsername } from '@shared/utils/validation';
import { translateValidationError } from '@shared/utils/validationMessages';
import bannerLoginImage from '../universal-login/images/banner-login-desktop.webp';

export type RequestPasswordResetScreenMode =
  | 'form'
  | 'sent'
  | 'tooSoon'
  | 'error'
  | 'technicalError';

interface RequestPasswordResetScreenProps {
  screen: RequestPasswordResetScreenMode;
  initialEmailOrUsername?: string;
  errorText?: string;
  onEmailOrUsernameChange: (data: Record<string, unknown>) => void;
  onSubmit: (emailOrUsername: string) => Promise<void>;
  onRetry: () => Promise<void>;
  onBackToLogin: () => void | Promise<void>;
  resetError: () => void;
}

const obscureEmailString = (email: string): string => {
  const atIndex = email.indexOf('@');

  if (atIndex === -1) {
    return email;
  }

  const username = email.substring(0, atIndex);
  const domain = email.substring(atIndex);

  if (username.length <= 1) {
    return `${username}***${domain}`;
  }

  if (username.length <= 3) {
    return `${username[0]}***${domain}`;
  }

  return `${username[0]}***${username[username.length - 1]}${domain}`;
};

const REQUEST_PASSWORD_RESET_SCREEN_TITLE = 'Log In - Request Password Reset';
const recoveryLayoutProps = {
  variant: 'banner',
  bannerImageDesktop: bannerLoginImage,
  subHeadingClasses: 'mx-auto max-w-[420px]',
} as const;

const RequestPasswordResetScreen: React.FC<RequestPasswordResetScreenProps> = ({
  screen,
  initialEmailOrUsername = '',
  errorText,
  onEmailOrUsernameChange,
  onSubmit,
  onRetry,
  onBackToLogin,
  resetError,
}) => {
  const { t } = useTranslation();
  usePublishScreenViewedEvent(REQUEST_PASSWORD_RESET_SCREEN_TITLE);
  const [emailOrUsername, setEmailOrUsername] = useState(
    initialEmailOrUsername
  );
  const [identifierError, setIdentifierError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isReturningToLogin, setIsReturningToLogin] = useState(false);

  useEffect(() => {
    setEmailOrUsername(initialEmailOrUsername);
  }, [initialEmailOrUsername]);

  const handleEmailOrUsernameChange = (value: string) => {
    const nextEmailOrUsername = value.trim();

    resetError();
    setIdentifierError(undefined);
    setEmailOrUsername(nextEmailOrUsername);
    onEmailOrUsernameChange({
      'form.emailOrUsername': nextEmailOrUsername,
    });
  };

  const handleSubmit = async () => {
    const trimmedEmailOrUsername = emailOrUsername.trim();
    const validation = validateEmailOrUsername(trimmedEmailOrUsername);

    if (!validation.isValid) {
      setIdentifierError(validation.error);
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(trimmedEmailOrUsername);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);

    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  const handleBackToLogin = async () => {
    setIsReturningToLogin(true);

    try {
      await onBackToLogin();
    } finally {
      setIsReturningToLogin(false);
    }
  };

  if (screen === 'form') {
    const localizedValidationError = translateValidationError(
      t,
      identifierError
    );

    return (
      <Layout
        {...recoveryLayoutProps}
        heading={t('Forgot password?')}
        subHeading={t(
          "Enter your email or username and we'll send you an email to reset your password."
        )}
      >
        <div className="space-y-6">
          <AuthForm>
            <InputText
              label={t('Email or username')}
              name="emailOrUsername"
              type="text"
              value={emailOrUsername}
              maxLength={100}
              autoCapitalize="none"
              autoComplete="username"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleEmailOrUsernameChange(event.target.value);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (
                  event.key === 'Enter' &&
                  emailOrUsername.length > 0 &&
                  !isSubmitting
                ) {
                  event.preventDefault();
                  void handleSubmit();
                }
              }}
              errorMessage={localizedValidationError || errorText}
              state={identifierError || errorText ? 'error' : 'default'}
            />
            <span role="status" className="sr-only">
              {localizedValidationError}
            </span>
            <Button
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                void handleSubmit();
              }}
              variant="primary"
              disabled={emailOrUsername.length === 0}
              label={t('Send email')}
              state={isSubmitting ? 'loading' : 'default'}
            />
          </AuthForm>

          <div className="flex justify-center">
            <Button
              variant="link"
              fullWidth={false}
              label={t('Back to log in')}
              onClick={() => {
                void handleBackToLogin();
              }}
              state={isReturningToLogin ? 'loading' : 'default'}
            />
          </div>
        </div>
      </Layout>
    );
  }

  const identifier = initialEmailOrUsername.trim();
  const sentSubHeading = identifier.includes('@')
    ? t('We just sent an email to reset your password to {{email}}.', {
        email: obscureEmailString(identifier),
      })
    : t('We just sent an email to reset your password for {{username}}.', {
        username: obscureEmailString(identifier),
      });

  const screenCopy: Record<
    Exclude<RequestPasswordResetScreenMode, 'form'>,
    { heading: string; subHeading: string; retryInteraction?: boolean }
  > = {
    sent: {
      heading: t('Check your email'),
      subHeading: sentSubHeading,
    },
    tooSoon: {
      heading: t('Check your email'),
      subHeading: t(
        'A reset email was requested recently. Please wait a minute before trying again.'
      ),
      retryInteraction: true,
    },
    error: {
      heading: t("Let's double-check your username or email address."),
      subHeading: t("Try again or contact support if you still can't log in."),
      retryInteraction: true,
    },
    technicalError: {
      heading: t('Something went wrong'),
      subHeading: t('Please refresh the page and try again.'),
      retryInteraction: true,
    },
  };
  const copy = screenCopy[screen];

  return (
    <Layout
      {...recoveryLayoutProps}
      heading={copy.heading}
      subHeading={copy.subHeading}
    >
      <div className="space-y-4">
        {copy.retryInteraction && (
          <Button
            onClick={() => {
              void handleRetry();
            }}
            variant="primary"
            label={t('Try again')}
            state={isRetrying ? 'loading' : 'default'}
          />
        )}
        <Button
          onClick={() => {
            void handleBackToLogin();
          }}
          variant={copy.retryInteraction ? 'outline' : 'primary'}
          label={t('Back to log in')}
          state={isReturningToLogin ? 'loading' : 'default'}
        />
      </div>
    </Layout>
  );
};

export default RequestPasswordResetScreen;
